import { useEffect, useMemo, useState } from 'react'
import {
  Sheet,
  SheetContent,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AlertTriangle, Loader2, Plus, Trash2, X } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { ComboboxCreatable } from '@/components/ui/combobox-creatable'
import {
  ClinicProgram,
  ClinicProgramDefaultMedication,
  ProgramMedication,
  clinicProgramService,
} from '@/services/clinicProgramService'

interface ClinicDefaultMedicationsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  clinicId: string
  program: ClinicProgram
  onSuccess: () => void
}

interface MedicationConfig {
  id: string
  medication_id: string
  medication_name: string
  dose: string
  sig: string
  quantity: number | ''
  days_supply: string
  refills_authorized: number | ''
  product_id?: string
  unit_of_measure?: string
}

const toConfig = (medication: ClinicProgramDefaultMedication): MedicationConfig => ({
  id: medication.id,
  medication_id: medication.medication_id,
  medication_name: medication.medication_name,
  dose: medication.dose,
  sig: medication.sig,
  quantity: medication.quantity,
  days_supply: medication.days_supply,
  refills_authorized: medication.refills_authorized,
  product_id: medication.product_id,
  unit_of_measure: medication.unit_of_measure,
})

const getMedicationDoses = (medicationId: string, medications: ProgramMedication[]): string[] => {
  const medication = medications.find(item => item.id === medicationId)
  return (medication?.doses || []).filter(Boolean)
}

const getMedicationSigs = (medicationId: string, medications: ProgramMedication[]): string[] => {
  const medication = medications.find(item => item.id === medicationId)
  return (medication?.sig || []).filter(Boolean)
}

const getMedicationQtyOptions = (medicationId: string, medications: ProgramMedication[]): string[] => {
  const medication = medications.find(item => item.id === medicationId)
  return ((medication as any)?.qty_options || []).map(String)
}

const ClinicDefaultMedicationsDialog = ({
  open,
  onOpenChange,
  clinicId,
  program,
  onSuccess,
}: ClinicDefaultMedicationsDialogProps) => {
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [availableMedications, setAvailableMedications] = useState<ProgramMedication[]>([])
  const [configuredMedications, setConfiguredMedications] = useState<MedicationConfig[]>([])
  const [validationErrors, setValidationErrors] = useState<Record<string, Record<string, string>>>({})

  const fetchData = async () => {
    try {
      setIsLoading(true)
      const medicationsResponse = await clinicProgramService.getProgramMedications(clinicId, program.id)
      setAvailableMedications(medicationsResponse.medications || [])

      const defaultsResponse = await clinicProgramService.getDefaultMedications(clinicId, program.id)
      setConfiguredMedications((defaultsResponse.defaultMedications || []).map(toConfig))
    } catch (error) {
      console.error(error)
      toast({
        title: 'Error',
        description: 'Failed to load medications',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!open) return
    fetchData()
  }, [open, clinicId, program.id])

  const selectedMedicationIds = useMemo(
    () => configuredMedications.map(config => config.medication_id).filter(Boolean),
    [configuredMedications],
  )

  const handleAddMedication = () => {
    setConfiguredMedications(prev => [
      ...prev,
      {
        id: `new-${Date.now()}`,
        medication_id: '',
        medication_name: '',
        dose: '',
        sig: '',
        quantity: 1,
        days_supply: '30',
        refills_authorized: 0,
        product_id: '',
        unit_of_measure: '',
      },
    ])
  }

  const handleRemoveMedication = (configId: string) => {
    setConfiguredMedications(prev => prev.filter(config => config.id !== configId))
  }

  const validateMedications = (): boolean => {
    const errors: Record<string, Record<string, string>> = {}
    let hasErrors = false

    configuredMedications.forEach(med => {
      const fieldErrors: Record<string, string> = {}
      if (!med.medication_id) fieldErrors.medication_id = 'Medication is required'
      if (!med.dose) fieldErrors.dose = 'Dose is required'
      if (!med.sig) fieldErrors.sig = 'Instructions are required'
      if (!med.quantity && med.quantity !== 0) fieldErrors.quantity = 'Quantity is required'

      if (Object.keys(fieldErrors).length > 0) {
        errors[med.id] = fieldErrors
        hasErrors = true
      }
    })

    setValidationErrors(errors)
    return !hasErrors
  }

  const clearFieldError = (configId: string, field: string) => {
    setValidationErrors(prev => {
      const updated = { ...prev }
      if (updated[configId]) {
        const { [field]: _, ...rest } = updated[configId]
        if (Object.keys(rest).length === 0) {
          delete updated[configId]
        } else {
          updated[configId] = rest
        }
      }
      return updated
    })
  }

  const handleMedicationChange = (configId: string, medicationId: string) => {
    const medication = availableMedications.find(item => item.id === medicationId)
    if (!medication) return

    const firstDose = medication.doses?.[0] || ''
    const firstSig = medication.sig?.[0] || ''

    setConfiguredMedications(prev =>
      prev.map(config =>
        config.id === configId
          ? {
              ...config,
              medication_id: medication.id,
              medication_name: medication.name,
              dose: firstDose,
              sig: firstSig,
              product_id: medication.product_id || medication.id,
              unit_of_measure: medication.unit_of_measure || '',
            }
          : config,
      ),
    )
    clearFieldError(configId, 'medication_id')
    if (firstDose) clearFieldError(configId, 'dose')
    if (firstSig) clearFieldError(configId, 'sig')
  }

  const handleDoseChange = (configId: string, dose: string) => {
    setConfiguredMedications(prev =>
      prev.map(config =>
        config.id === configId ? { ...config, dose } : config
      ),
    )
    if (dose) clearFieldError(configId, 'dose')
  }

  const handleFieldChange = (configId: string, field: keyof MedicationConfig, value: string | number) => {
    setConfiguredMedications(prev =>
      prev.map(config => (config.id === configId ? { ...config, [field]: value } : config)),
    )
    if (value !== '' && value !== null) clearFieldError(configId, field)
  }

  const handleSave = async () => {
    if (configuredMedications.length > 0 && !validateMedications()) {
      toast({
        title: 'Validation Error',
        description: 'Please complete all required fields',
        variant: 'destructive',
      })
      return
    }

    try {
      setIsSaving(true)
      await clinicProgramService.saveDefaultMedications(
        clinicId,
        program.id,
        configuredMedications.map(medication => ({
          medication_id: medication.medication_id,
          medication_name: medication.medication_name,
          dose: medication.dose,
          sig: medication.sig,
          quantity: medication.quantity === '' ? 1 : medication.quantity,
          days_supply: medication.days_supply,
          refills_authorized:
            medication.refills_authorized === '' ? 0 : medication.refills_authorized,
          product_id: medication.product_id,
          unit_of_measure: medication.unit_of_measure,
        })),
      )

      toast({
        title: 'Success',
        description: 'Default medications saved successfully',
        variant: 'success',
      })
      await fetchData()
      onSuccess()
      onOpenChange(false)
    } catch (error) {
      console.error(error)
      toast({
        title: 'Error',
        description: 'Failed to save default medications',
        variant: 'destructive',
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full !max-w-[720px] flex flex-col p-0 gap-0">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Default Medications</h2>
            <p className="text-sm text-slate-500">{program.title}</p>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors"
          >
            <X className="h-5 w-5 text-slate-600" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {isLoading && (
            <div className="flex justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
            </div>
          )}

          {!isLoading && availableMedications.length === 0 && configuredMedications.length === 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <p className="text-sm text-amber-800">
                No medications available. Assign templates first.
              </p>
            </div>
          )}

          {!isLoading &&
            configuredMedications.map((config, index) => (
              <div key={config.id} className="rounded-lg border border-slate-200 p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <p className="text-sm font-medium text-slate-700">Medication #{index + 1}</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleRemoveMedication(config.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-600">Medication <span className="text-red-500">*</span></Label>
                    <ComboboxCreatable
                      options={availableMedications
                        .filter(
                          medication =>
                            medication.id === config.medication_id ||
                            !selectedMedicationIds.includes(medication.id),
                        )
                        .map(medication => ({
                          value: medication.id,
                          label: medication.name,
                        }))}
                      value={config.medication_id || ''}
                      onChange={value => value && handleMedicationChange(config.id, value)}
                      placeholder="Type medication name"
                    />
                    {validationErrors[config.id]?.medication_id && (
                      <p className="text-xs text-red-500">{validationErrors[config.id].medication_id}</p>
                    )}
                  </div>

                  {config.medication_id && (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-xs text-slate-600">Dose <span className="text-red-500">*</span></Label>
                          <ComboboxCreatable
                            options={getMedicationDoses(config.medication_id, availableMedications).map(dose => ({
                              value: dose,
                              label: dose,
                            }))}
                            value={config.dose || ''}
                            onChange={value => handleDoseChange(config.id, value)}
                            placeholder="Type dose"
                          />
                          {validationErrors[config.id]?.dose && (
                            <p className="text-xs text-red-500">{validationErrors[config.id].dose}</p>
                          )}
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs text-slate-600">Refills</Label>
                          <Input
                            type="number"
                            min="0"
                            value={config.refills_authorized}
                            onChange={e => {
                              const nextValue = e.target.value
                              handleFieldChange(
                                config.id,
                                'refills_authorized',
                                nextValue === '' ? '' : parseInt(nextValue, 10),
                              )
                            }}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-xs text-slate-600">Quantity <span className="text-red-500">*</span></Label>
                          <ComboboxCreatable
                            options={getMedicationQtyOptions(config.medication_id, availableMedications).map(q => ({
                              value: q,
                              label: q,
                            }))}
                            value={String(config.quantity) || ''}
                            onChange={value => handleFieldChange(config.id, 'quantity', value)}
                            placeholder="Type quantity"
                          />
                          {validationErrors[config.id]?.quantity && (
                            <p className="text-xs text-red-500">{validationErrors[config.id].quantity}</p>
                          )}
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs text-slate-600">Days Supply</Label>
                          <Input
                            value={config.days_supply}
                            onChange={e => handleFieldChange(config.id, 'days_supply', e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Instructions <span className="text-red-500">*</span></Label>
                        <ComboboxCreatable
                          options={getMedicationSigs(config.medication_id, availableMedications).map(sig => ({
                            value: sig,
                            label: sig,
                          }))}
                          value={config.sig || ''}
                          onChange={value => handleFieldChange(config.id, 'sig', value)}
                          placeholder="Type instruction"
                        />
                        {validationErrors[config.id]?.sig && (
                          <p className="text-xs text-red-500">{validationErrors[config.id].sig}</p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}

          {!isLoading && availableMedications.length > 0 && (
            <Button
              variant="outline"
              onClick={handleAddMedication}
              disabled={selectedMedicationIds.length >= availableMedications.length}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Medication
            </Button>
          )}
        </div>

        <div className="flex-shrink-0 px-6 py-4 border-t border-slate-200 flex items-center justify-between">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || isLoading}>
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save Default Medications
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}

export default ClinicDefaultMedicationsDialog
